import axios from 'axios';
import { Config } from '../config/config';

interface VideoItem {
  id: string;
  fileName: string;
  timeStamp: string;
  cameraName: string;
  waschstrasse: string;
  richtigAusgeloest: boolean | null;
  hasRating: boolean;
  thumbnailUrl: string;
  videoUrl: string;
  account?: string;
  cameraRef: string;
  blurhash?: string;
}

// Fix: Handle the case when Config.apiRoot does or doesn't include '/api' (matching other APIs)
const API_BASE_URL = Config.apiRoot.endsWith('/api') 
  ? Config.apiRoot
  : `${Config.apiRoot}/api`;

console.log('📱 Archive API (TS) initialized with API base URL:', API_BASE_URL);
console.log('🔧 Original Config.apiRoot:', Config.apiRoot);

// Konfiguration für API-Anfragen mit Session-Token
const getConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

/**
 * Fetches the latest date with available videos
 * @returns A promise that resolves to a Date object for the latest date with videos, or null if none are found
 */
export const fetchLatestVideoDate = async (): Promise<Date | null> => {
  try {
    console.log('🔍 Fetching latest video date');
    const cacheBuster = new Date().getTime();
    
    // Use the dedicated API endpoint for finding the latest date with videos
    const requestUrl = `${API_BASE_URL}/videos/archive/latest-date?_=${cacheBuster}`;
    console.log('🔗 Full request URL:', requestUrl);
    
    const response = await axios.get(
      requestUrl,
      {
        ...getConfig(),
        headers: {
          ...getConfig().headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }
    );
    
    console.log('📦 Latest date response:', response.data);
    
    if (response.data && response.data.success && response.data.data) {
      if (response.data.data.latestDate) {
        console.log(`✅ Found latest date with videos: ${response.data.data.latestDate}`);
        return new Date(response.data.data.latestDate);
      } else {
        console.log('⚠️ No date with videos found');
        return null;
      }
    }
    
    console.warn('⚠️ Unexpected response format from latest-date endpoint');
    return null;
  } catch (error) {
    console.error('❌ Error in fetchLatestVideoDate:', error);
    return null;
  }
};

/**
 * Fetches videos for a specific date
 * @param date The date to fetch videos for
 * @returns A promise that resolves to an array of video items
 */
export const fetchVideosByDate = async (date: Date): Promise<VideoItem[]> => {
  try {
    // Use local timezone date formatting to prevent timezone shift
    // This ensures the date sent to the API is the same as what the user selected
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    console.log('📅 Local date selected:', date.toLocaleDateString());
    console.log('📅 Formatted date for API:', formattedDate);
    
    // Add a cache-busting timestamp to prevent 304 Not Modified responses
    const cacheBuster = new Date().getTime();
    const requestUrl = `${API_BASE_URL}/videos/archive/date/${formattedDate}?_=${cacheBuster}`;
    
    console.log('🔍 Fetching videos from:', requestUrl);
    console.log('🔑 Auth token:', localStorage.getItem('token') ? 'Token present' : 'No token found');
    
    const startTime = performance.now();
    const response = await axios.get(
      requestUrl,
      {
        ...getConfig(),
        headers: {
          ...getConfig().headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }
    );
    const endTime = performance.now();
    
    console.log(`⏱️ Request completed in ${Math.round(endTime - startTime)}ms`);
    console.log('📊 API Response:', response.status, response.statusText);
    console.log('📦 Response data:', response.data);
    
    // Map the response data to our frontend model
    if (response.data && response.data.data) {
      return response.data.data.map((video: any) => ({
        id: video._id,
        fileName: video.fileName,
        timeStamp: video.timestamp,
        cameraName: video.kameraName || video.cameraRef,
        waschstrasse: video.waschstrasse,
        richtigAusgeloest: video.richtigAusgeloest,
        hasRating: video.hasRating,
        thumbnailUrl: video.thumbnailUrl,
        videoUrl: video.videoUrl,
        account: video.account,
        cameraRef: video.cameraRef,
        blurhash: video.blurhash
      }));
    }
    
    return [];
  } catch (error: any) {
    console.error('❌ Error fetching videos:', error);
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('📡 Error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('📡 No response received, request details:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('🔄 Request setup error:', error.message);
    }
    
    throw error;
  }
};

/**
 * Rate a video
 * @param videoId The ID of the video to rate
 * @param rating The rating to give the video (0-5)
 */
export const rateVideo = async (videoId: string, rating: number): Promise<void> => {
  try {
    await axios.post(
      `${API_BASE_URL}/archive/videos/${videoId}/rate`, 
      { rating },
      getConfig()
    );
  } catch (error) {
    console.error('Error rating video:', error);
    throw error;
  }
};

/**
 * Get video metadata by ID
 * @param videoId The ID of the video
 * @returns A promise that resolves to the video metadata
 */
export const getVideoById = async (videoId: string): Promise<VideoItem> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/videos/${videoId}`,
      getConfig()
    );
    
    const video = response.data.data;
    return {
      id: video._id,
      fileName: video.fileName,
      timeStamp: video.timestamp,
      cameraName: video.kameraName,
      waschstrasse: video.waschstrasse,
      richtigAusgeloest: video.richtigAusgeloest,
      hasRating: video.hasRating,
      thumbnailUrl: video.thumbnailUrl,
      videoUrl: video.videoUrl,
      account: video.account,
      cameraRef: video.cameraRef,
      blurhash: video.blurhash
    };
  } catch (error) {
    console.error('Error fetching video details:', error);
    throw error;
  }
};

/**
 * Finds the next date (future) that has videos
 * @param currentDate The reference date
 * @returns The next date with videos, or null if none found
 */
export const findNextDateWithVideos = async (currentDate: Date): Promise<Date | null> => {
  try {
    console.log('🔍 Finding next date with videos after:', currentDate.toISOString());
    const cacheBuster = new Date().getTime();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    // Use the dedicated API endpoint for finding the next date with videos
    const requestUrl = `${API_BASE_URL}/videos/archive/next-date/${formattedDate}?_=${cacheBuster}`;
    const response = await axios.get(
      requestUrl,
      {
        ...getConfig(),
        headers: {
          ...getConfig().headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }
    );
    
    if (response.data && response.data.success && response.data.data) {
      if (response.data.data.nextDate) {
        console.log(`✅ Found next date with videos: ${response.data.data.nextDate}`);
        return new Date(response.data.data.nextDate);
      } else {
        console.log('⚠️ No future date with videos found');
        return null;
      }
    }
    
    console.warn('⚠️ Unexpected response format from next-date endpoint');
    return null;
  } catch (error) {
    console.error('❌ Error finding next date with videos:', error);
    return null;
  }
};

/**
 * Finds the previous date (past) that has videos
 * @param currentDate The reference date
 * @returns The previous date with videos, or null if none found
 */
export const findPrevDateWithVideos = async (currentDate: Date): Promise<Date | null> => {
  try {
    console.log('🔍 Finding previous date with videos before:', currentDate.toISOString());
    const cacheBuster = new Date().getTime();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    // Use the dedicated API endpoint for finding the previous date with videos
    const requestUrl = `${API_BASE_URL}/videos/archive/prev-date/${formattedDate}?_=${cacheBuster}`;
    const response = await axios.get(
      requestUrl,
      {
        ...getConfig(),
        headers: {
          ...getConfig().headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }
    );
    
    if (response.data && response.data.success && response.data.data) {
      if (response.data.data.prevDate) {
        console.log(`✅ Found previous date with videos: ${response.data.data.prevDate}`);
        return new Date(response.data.data.prevDate);
      } else {
        console.log('⚠️ No previous date with videos found');
        return null;
      }
    }
    
    console.warn('⚠️ Unexpected response format from prev-date endpoint');
    return null;
  } catch (error) {
    console.error('❌ Error finding previous date with videos:', error);
    return null;
  }
}; 